import axios from "axios";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Enable credentials for all requests (cookie-based auth)
axios.defaults.withCredentials = true;

// ─────────────────────────────────────────────────────────────────────────
// Global 401 → refresh → retry interceptor.
// The backend issues a 15-min access token + 7-day refresh token as httponly
// cookies. Without this, the moment the access cookie expires the next call
// 401s and the user is dumped to login. This interceptor catches those
// 401s once, refreshes silently, and replays the original request.
//
// Design:
//   * Skip the auth endpoints themselves — their 401s are real.
//   * Never retry a request twice — the _authRetry flag guards against loops.
//   * Single-flight — if N requests 401 concurrently, only ONE POST /refresh
//     goes out. The others await the same in-flight promise, then replay.
//   * Refresh success → replay all queued requests.
//   * Refresh failure → notify AuthContext once, replay nothing, propagate
//     the original 401 so callers can react normally. Once a refresh has
//     DEFINITIVELY failed, `_authCleared` is set and further refresh attempts
//     are short-circuited until the next successful login clears the flag.
//     That stops a tab loaded with two dead cookies from firing one refresh
//     per failing request.
// ─────────────────────────────────────────────────────────────────────────
let _refreshInFlight = null;
let _authCleared = false;

function _shouldSkipRefresh(url) {
  if (!url) return true;
  // Match the auth paths regardless of whether url is absolute or relative.
  return /\/api\/auth\/(login|register|refresh|logout)(\?|$|\/)/.test(url);
}

function _notifyAuthCleared() {
  // Fire once; AuthContext listens and clears local user state. Using an
  // event keeps api.js free of a direct import cycle with AuthContext.
  try {
    window.dispatchEvent(new CustomEvent("mmntm:auth-cleared"));
  } catch { /* ignore — SSR/test envs without window */ }
}

// Shared single-flight refresh. Both the interceptor (below) AND the
// AuthContext boot path call this so mount + interceptor concurrent paths
// can never issue two refreshes at once.
export function refreshSession() {
  if (_authCleared) {
    return Promise.reject(new Error("auth cleared"));
  }
  if (!_refreshInFlight) {
    _refreshInFlight = axios
      .post(`${API}/auth/refresh`, null, { withCredentials: true, _authRetry: true })
      .then(() => true)
      .catch((refreshErr) => {
        _authCleared = true;
        _notifyAuthCleared();
        throw refreshErr;
      })
      .finally(() => {
        // Clear the promise slot so a later expiry can trigger another
        // refresh cleanly once the user has re-authenticated.
        _refreshInFlight = null;
      });
  }
  return _refreshInFlight;
}

// Called by AuthContext on successful login/register/logout so the guard
// is re-armed for the fresh session.
export function resetAuthClearedFlag() {
  _authCleared = false;
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;

    // Only handle 401s from our own backend where a retry makes sense.
    if (status !== 401 || !original || original._authRetry) {
      return Promise.reject(error);
    }
    if (_shouldSkipRefresh(original.url)) {
      return Promise.reject(error);
    }
    // If a previous refresh already failed and the user hasn't re-authed,
    // don't burn more refresh calls on this dead session.
    if (_authCleared) {
      return Promise.reject(error);
    }

    original._authRetry = true;

    try {
      await refreshSession();
    } catch {
      // Refresh itself failed → propagate the original 401.
      return Promise.reject(error);
    }
    // Replay the original request with the freshly-set cookies.
    return axios(original);
  },
);

// Global interceptor: surface milestone unlocks as a toast.
// Handlers append `milestones_unlocked: [{name, icon, xp_reward, ...}]` to their
// JSON response when a check_milestones() call returned new unlocks. We don't
// want every UI mutation site to do its own toast plumbing — handle it here.
axios.interceptors.response.use((response) => {
  const unlocked = response?.data?.milestones_unlocked;
  if (Array.isArray(unlocked) && unlocked.length > 0) {
    for (const m of unlocked) {
      track("reward_earned", { type: "milestone" });
      toast.success(
        `${m.icon || "🏆"} Achievement Unlocked: ${m.name} — +${m.xp_reward} XP`,
        { duration: 5000 },
      );
    }
  }
  // First-task confetti — fires once per user, ever. Delegated to
  // FirstTaskCelebrate component so confetti import stays out of api.js.
  if (response?.data?.first_completion_celebration) {
    window.dispatchEvent(new CustomEvent("mmntm:celebrate-first-task"));
  }
  return response;
});

export const api = {
  // Generic
  get: (path) => axios.get(`${API}${path}`).then(r => r.data),
  put: (path, data) => axios.put(`${API}${path}`, data).then(r => r.data),
  post: (path, data) => axios.post(`${API}${path}`, data).then(r => r.data),
  patch: (path, data) => axios.patch(`${API}${path}`, data).then(r => r.data),

  // Search
  search: (params) => axios.get(`${API}/search`, { params }).then(r => r.data),
  getRecentSearches: () => axios.get(`${API}/search/recent`).then(r => r.data),
  clearRecentSearches: () => axios.delete(`${API}/search/recent`).then(r => r.data),
  deleteRecentSearch: (query) => axios.delete(`${API}/search/recent/${encodeURIComponent(query)}`).then(r => r.data),

  // Milestones / XP
  getMilestones: () => axios.get(`${API}/milestones`).then(r => r.data),
  getMilestonesSummary: () => axios.get(`${API}/milestones/summary`).then(r => r.data),

  getProjects: () => axios.get(`${API}/projects`).then(r => r.data),
  getProject: (id) => axios.get(`${API}/projects/${id}`).then(r => r.data),
  createProject: (data) => axios.post(`${API}/projects`, data).then(r => r.data),
  updateProject: (id, data) => axios.put(`${API}/projects/${id}`, data).then(r => r.data),
  deleteProject: (id) => axios.delete(`${API}/projects/${id}`).then(r => r.data),

  startSession: (data) => axios.post(`${API}/sessions/start`, data).then(r => r.data),
  stopSession: (id, data) => axios.put(`${API}/sessions/${id}/stop`, data).then(r => r.data),
  getSessions: (params) => axios.get(`${API}/sessions`, { params }).then(r => r.data),

  getDashboardStats: () => axios.get(`${API}/stats/dashboard`).then(r => r.data),
  getProjectStats: (id) => axios.get(`${API}/projects/${id}/stats`).then(r => r.data),

  getTasks: (params) => axios.get(`${API}/tasks`, { params }).then(r => r.data),
  getTask: (id) => axios.get(`${API}/tasks/${id}`).then(r => r.data),
  createTask: (data) => axios.post(`${API}/tasks`, data).then(r => r.data),
  updateTask: (id, data) => axios.put(`${API}/tasks/${id}`, data).then(r => r.data),
  deleteTask: (id) => axios.delete(`${API}/tasks/${id}`).then(r => r.data),
  toggleTask: (id) => axios.put(`${API}/tasks/${id}/toggle`).then(r => r.data),
  archiveTask: (id) => axios.put(`${API}/tasks/${id}/archive`).then(r => r.data),
  getTaskStats: () => axios.get(`${API}/tasks/stats/summary`).then(r => r.data),
  startTaskTimer: (id) => axios.put(`${API}/tasks/${id}/timer/start`).then(r => r.data),
  pauseTaskTimer: (id) => axios.put(`${API}/tasks/${id}/timer/pause`).then(r => r.data),
  stopTaskTimer: (id) => axios.put(`${API}/tasks/${id}/timer/stop`).then(r => r.data),

  // Boxes
  getBoxes: () => axios.get(`${API}/boxes`).then(r => r.data),
  getBox: (id) => axios.get(`${API}/boxes/${id}`).then(r => r.data),
  createBox: (data) => axios.post(`${API}/boxes`, data).then(r => r.data),
  updateBox: (id, data) => axios.put(`${API}/boxes/${id}`, data).then(r => r.data),
  deleteBox: (id) => axios.delete(`${API}/boxes/${id}`).then(r => r.data),
  duplicateBox: (id) => axios.post(`${API}/boxes/${id}/duplicate`).then(r => r.data),
  updateBoxLayout: (data) => axios.put(`${API}/boxes/layout`, data).then(r => r.data),

  // Task reorder
  reorderTasks: (data) => axios.put(`${API}/tasks/reorder`, data).then(r => r.data),

  // Focus sessions
  createFocusSession: (data) => axios.post(`${API}/focus-sessions`, data).then(r => r.data),
  getFocusSessions: (params) => axios.get(`${API}/focus-sessions`, { params }).then(r => r.data),
  updateFocusSession: (id, data) => axios.put(`${API}/focus-sessions/${id}`, data).then(r => r.data),
  getFocusStats: () => axios.get(`${API}/focus-sessions/stats`).then(r => r.data),

  // Archive
  getArchivedTasks: () => axios.get(`${API}/tasks/archived/list`).then(r => r.data),
  unarchiveTask: (id) => axios.put(`${API}/tasks/${id}/unarchive`).then(r => r.data),

  // Recurring
  resetRecurringTasks: () => axios.post(`${API}/tasks/recurring/reset`).then(r => r.data),

  // Subtasks
  addSubtask: (taskId, data) => axios.post(`${API}/tasks/${taskId}/subtasks`, data).then(r => r.data),
  toggleSubtask: (taskId, subtaskId) => axios.put(`${API}/tasks/${taskId}/subtasks/${subtaskId}/toggle`).then(r => r.data),
  deleteSubtask: (taskId, subtaskId) => axios.delete(`${API}/tasks/${taskId}/subtasks/${subtaskId}`).then(r => r.data),
  renameSubtask: (taskId, subtaskId, title) => axios.put(`${API}/tasks/${taskId}/subtasks/${subtaskId}`, { title }).then(r => r.data),
  reorderSubtasks: (taskId, data) => axios.put(`${API}/tasks/${taskId}/subtasks/reorder`, data).then(r => r.data),
  completeTaskWithSubtasks: (taskId) => axios.put(`${API}/tasks/${taskId}/complete-all`).then(r => r.data),

  // Notifications
  getDueSoonTasks: () => axios.get(`${API}/tasks/due-soon`).then(r => r.data),
  getNotifications: (params) => axios.get(`${API}/notifications`, { params }).then(r => r.data),
  markNotificationRead: (id) => axios.put(`${API}/notifications/${id}/read`).then(r => r.data),
  markAllNotificationsRead: () => axios.put(`${API}/notifications/read-all`).then(r => r.data),
  dismissNotification: (id) => axios.delete(`${API}/notifications/${id}`).then(r => r.data),
  checkReminders: () => axios.post(`${API}/notifications/check-reminders`).then(r => r.data),
  getNotificationPreferences: () => axios.get(`${API}/notifications/preferences`).then(r => r.data),
  updateNotificationPreferences: (data) => axios.put(`${API}/notifications/preferences`, data).then(r => r.data),
  getReminderSettings: () => axios.get(`${API}/settings/reminders`).then(r => r.data),
  updateReminderSettings: (data) => axios.put(`${API}/settings/reminders`, data).then(r => r.data),

  // Preferences
  getThemeDetectionPrefs: () => axios.get(`${API}/preferences/theme-detection`).then(r => r.data),
  updateThemeDetectionPrefs: (data) => axios.put(`${API}/preferences/theme-detection`, data).then(r => r.data),

  // Export
  exportTasksCsv: () => `${API}/export/tasks/csv`,
  exportStatsCsv: () => `${API}/export/stats/csv`,

  // Game Mode
  getGameProgress: () => axios.get(`${API}/game/progress`).then(r => r.data),
  createHabit: (data) => axios.post(`${API}/game/habits`, data).then(r => r.data),
  getHabits: () => axios.get(`${API}/game/habits`).then(r => r.data),
  updateHabit: (id, data) => axios.put(`${API}/game/habits/${id}`, data).then(r => r.data),
  deleteHabit: (id) => axios.delete(`${API}/game/habits/${id}`).then(r => r.data),
  completeHabit: (id) => axios.post(`${API}/game/habits/${id}/complete`).then(r => r.data),
  getTodayQuests: () => axios.get(`${API}/game/quests/today`).then(r => r.data),
  completeQuest: (id) => axios.post(`${API}/game/quests/complete/${id}`).then(r => r.data),
  rerollQuest: (data) => axios.post(`${API}/game/quests/reroll`, data).then(r => r.data),
  getRewards: () => axios.get(`${API}/game/rewards`).then(r => r.data),
  getSkillTree: () => axios.get(`${API}/game/skill-tree`).then(r => r.data),
  unlockSkillNode: (nodeId) => axios.post(`${API}/game/skill-tree/${nodeId}/unlock`).then(r => r.data),
  getWeeklyBoss: () => axios.get(`${API}/game/boss`).then(r => r.data),
  updateBossProgress: () => axios.post(`${API}/game/boss/progress`).then(r => r.data),
  getSeasonalEvent: () => axios.get(`${API}/game/seasonal`).then(r => r.data),
  updateSeasonalProgress: () => axios.post(`${API}/game/seasonal/progress`).then(r => r.data),

  // Focus Lock
  getFocusLockConfig: () => axios.get(`${API}/focus-lock/config`).then(r => r.data),
  saveFocusLockConfig: (data) => axios.post(`${API}/focus-lock/config`, data).then(r => r.data),
  getFocusLockStatus: () => axios.get(`${API}/focus-lock/status`).then(r => r.data),
  focusLockOverride: () => axios.post(`${API}/focus-lock/override`).then(r => r.data),

  // Focus Streaks
  getStreaks: () => axios.get(`${API}/streaks`).then(r => r.data),
  logFocusSession: () => axios.post(`${API}/streaks/log-focus`).then(r => r.data),
  getStreakMilestones: () => axios.get(`${API}/streaks/milestones`).then(r => r.data),

  // Daily Usage
  getDailyUsage: () => axios.get(`${API}/usage/daily`).then(r => r.data),

  // Referrals
  getReferralInfo: () => axios.get(`${API}/referrals`).then(r => r.data),
  claimReferralReward: (milestone) => axios.post(`${API}/referrals/claim/${milestone}`).then(r => r.data),

  // Boosters
  getBoosters: () => axios.get(`${API}/boosters`).then(r => r.data),

  // Theme Progression
  getThemeChoices: () => axios.get(`${API}/game/theme-choices`).then(r => r.data),
  selectThemeChoice: (themeId) => axios.post(`${API}/game/theme-choices/select`, { theme_id: themeId }).then(r => r.data),

  // Public: Features / Version / What's New
  getPublicFeatures: () => axios.get(`${API}/features`).then(r => r.data),
  getCurrentVersion: () => axios.get(`${API}/version`).then(r => r.data),
  getWhatsNew: () => axios.get(`${API}/whats-new`).then(r => r.data),

  // Admin: Feature Flags
  adminGetFeatureFlags: () => axios.get(`${API}/admin/feature-flags`).then(r => r.data),
  adminCreateFeatureFlag: (data) => axios.post(`${API}/admin/feature-flags`, data).then(r => r.data),
  adminUpdateFeatureFlag: (id, data) => axios.put(`${API}/admin/feature-flags/${id}`, data).then(r => r.data),
  adminDeleteFeatureFlag: (id) => axios.delete(`${API}/admin/feature-flags/${id}`).then(r => r.data),
  adminReleaseFeatureFlag: (id) => axios.post(`${API}/admin/feature-flags/${id}/release`).then(r => r.data),
  adminRollbackFeatureFlag: (id) => axios.post(`${API}/admin/feature-flags/${id}/rollback`).then(r => r.data),

  // Admin: Versions
  adminGetVersions: () => axios.get(`${API}/admin/versions`).then(r => r.data),
  adminCreateVersion: (data) => axios.post(`${API}/admin/versions`, data).then(r => r.data),
  adminUpdateVersion: (id, data) => axios.put(`${API}/admin/versions/${id}`, data).then(r => r.data),
  adminReleaseVersion: (id) => axios.post(`${API}/admin/versions/${id}/release`).then(r => r.data),
  adminDeleteVersion: (id) => axios.delete(`${API}/admin/versions/${id}`).then(r => r.data),

  // Admin: Dashboard & Simulation
  adminGetDashboardStats: () => axios.get(`${API}/admin/dashboard-stats`).then(r => r.data),
  adminSimulateUser: (tier) => axios.get(`${API}/admin/simulate-user/${tier}`).then(r => r.data),

  // Sync
  getSyncStatus: () => axios.get(`${API}/sync/status`).then(r => r.data),
  syncHeartbeat: (data) => axios.post(`${API}/sync/heartbeat`, data).then(r => r.data),
  getSyncChanges: (since) => axios.get(`${API}/sync/changes`, { params: { since } }).then(r => r.data),

  // Push Notifications
  subscribePush: (data) => axios.post(`${API}/push/subscribe`, data).then(r => r.data),
  unsubscribePush: (data) => axios.delete(`${API}/push/subscribe`, { data }).then(r => r.data),
  getPushSubscriptions: () => axios.get(`${API}/push/subscriptions`).then(r => r.data),
  getVapidKey: () => axios.get(`${API}/push/vapid-key`).then(r => r.data),

  // Collaboration / Sharing
  inviteToProject: (data) => axios.post(`${API}/collaborate/invite`, data).then(r => r.data),
  getSentInvites: () => axios.get(`${API}/collaborate/invites/sent`).then(r => r.data),
  getReceivedInvites: () => axios.get(`${API}/collaborate/invites/received`).then(r => r.data),
  acceptInvite: (id) => axios.put(`${API}/collaborate/invites/${id}/accept`).then(r => r.data),
  declineInvite: (id) => axios.put(`${API}/collaborate/invites/${id}/decline`).then(r => r.data),
  getCollaborators: (projectId) => axios.get(`${API}/collaborate/projects/${projectId}/collaborators`).then(r => r.data),
  removeCollaborator: (projectId, userId) => axios.delete(`${API}/collaborate/projects/${projectId}/collaborators/${userId}`).then(r => r.data),
  assignTask: (taskId, data) => axios.put(`${API}/collaborate/tasks/${taskId}/assign`, data).then(r => r.data),
  unassignTask: (taskId) => axios.put(`${API}/collaborate/tasks/${taskId}/unassign`).then(r => r.data),

  // Team collaboration (Prompt 7)
  getProjectMembers: (projectId) => axios.get(`${API}/team/projects/${projectId}/members`).then(r => r.data),
  changeProjectMemberRole: (projectId, userId, data) => axios.put(`${API}/team/projects/${projectId}/members/${userId}/role`, data).then(r => r.data),
  removeProjectMember: (projectId, userId) => axios.delete(`${API}/team/projects/${projectId}/members/${userId}`).then(r => r.data),
  leaveProject: (projectId) => axios.post(`${API}/team/projects/${projectId}/leave`).then(r => r.data),
  transferOwnership: (projectId, data) => axios.post(`${API}/team/projects/${projectId}/transfer-ownership`, data).then(r => r.data),
  sendTeamInvitations: (data) => axios.post(`${API}/team/invitations`, data).then(r => r.data),
  getMyPendingInvites: () => axios.get(`${API}/team/invitations/pending`).then(r => r.data),
  getProjectInvitations: (projectId) => axios.get(`${API}/team/invitations/project/${projectId}`).then(r => r.data),
  acceptTeamInvite: (inviteId) => axios.post(`${API}/team/invitations/${inviteId}/accept`).then(r => r.data),
  declineTeamInvite: (inviteId) => axios.post(`${API}/team/invitations/${inviteId}/decline`).then(r => r.data),
  revokeTeamInvite: (inviteId) => axios.post(`${API}/team/invitations/${inviteId}/revoke`).then(r => r.data),
  resendTeamInvite: (inviteId) => axios.post(`${API}/team/invitations/${inviteId}/resend`).then(r => r.data),
  getInviteByToken: (token) => axios.get(`${API}/team/invitations/token/${token}`).then(r => r.data),
  acceptInviteByToken: (token) => axios.post(`${API}/team/invitations/token/${token}/accept`).then(r => r.data),

  // Password reset (Bundle E-1) — public, no session required
  forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }).then(r => r.data),
  resetPassword: (token, password) => axios.post(`${API}/auth/reset-password`, { token, password }).then(r => r.data),
  validateResetToken: (token) => axios.post(`${API}/auth/password-reset/validate`, { token }).then(r => r.data),
  twoFactorSetup: (code) => axios.post(`${API}/auth/2fa/setup`, code ? { code } : {}).then(r => r.data),
  twoFactorConfirm: (code) => axios.post(`${API}/auth/2fa/confirm`, { code }).then(r => r.data),
  twoFactorDisable: (password, code) => axios.post(`${API}/auth/2fa/disable`, { password, code }).then(r => r.data),
  twoFactorRegenerate: (code) => axios.post(`${API}/auth/2fa/recovery/regenerate`, { code }).then(r => r.data),
  // Bundle P-1: passkeys
  passkeyRegisterOptions: (auth) => axios.post(`${API}/auth/passkeys/register/options`, auth || {}).then(r => r.data),
  passkeyRegisterVerify: (credential, name) => axios.post(`${API}/auth/passkeys/register/verify`, { credential, name }).then(r => r.data),
  passkeyLoginOptions: (email) => axios.post(`${API}/auth/passkeys/login/options`, email ? { email } : {}).then(r => r.data),
  passkeyLoginVerify: (credential) => axios.post(`${API}/auth/passkeys/login/verify`, { credential }).then(r => r.data),
  listPasskeys: () => axios.get(`${API}/auth/passkeys`).then(r => r.data),
  renamePasskey: (id, name) => axios.patch(`${API}/auth/passkeys/${id}`, { name }).then(r => r.data),
  deletePasskey: (id, auth) => axios.delete(`${API}/auth/passkeys/${id}`, { data: auth }).then(r => r.data),
  // Bundle A-1: owner analytics + step-up
  stepUp: (password, code) => axios.post(`${API}/auth/step-up`, { password, code }).then(r => r.data),
  stepDown: () => axios.post(`${API}/auth/step-down`).then(r => r.data),
  adminOverview: () => axios.get(`${API}/admin/overview`).then(r => r.data),
  adminTimeseries: (metric, days = 30) => axios.get(`${API}/admin/timeseries`, { params: { metric, days } }).then(r => r.data),
  adminRetention: () => axios.get(`${API}/admin/retention`).then(r => r.data),
  adminActivation: () => axios.get(`${API}/admin/activation`).then(r => r.data),
  adminUsers: (limit, offset, includeProbes = false, q = "", verified = "all") => axios.get(`${API}/admin/users`, { params: { limit, offset, include_probes: includeProbes, q, verified } }).then(r => r.data),
  resendVerification: () => axios.post(`${API}/auth/verify-email/resend`).then(r => r.data),
  adminFeedback: (limit, offset) => axios.get(`${API}/admin/feedback`, { params: { limit, offset } }).then(r => r.data),
  // Bundle A-2: dev-console diagnostics + account deletion
  adminDiagnosticsEmail: () => axios.get(`${API}/admin/diagnostics/email`).then(r => r.data),
  adminDeleteUser: (userId, reason) => axios.delete(`${API}/admin/users/${userId}`, { data: { reason } }).then(r => r.data),
  adminPurgeUsers: (body) => axios.post(`${API}/admin/users/purge`, body).then(r => r.data),
  deleteMyAccount: (password, code) => axios.delete(`${API}/auth/me`, { data: code ? { password, code } : { password } }).then(r => r.data),
  // Bundle A-3: 30-day grace period
  adminDeletedAccounts: () => axios.get(`${API}/admin/deleted-accounts`).then(r => r.data),
  // Bundle F-2: audit trail
  adminAudit: (params) => axios.get(`${API}/admin/audit`, { params }).then(r => r.data),
  adminAuditExport: (params) => axios.get(`${API}/admin/audit/export.csv`, { params, responseType: "blob" }).then(r => r.data),
  adminRestoreDeleted: (userId) => axios.post(`${API}/admin/deleted-accounts/${userId}/restore`).then(r => r.data),
  adminPurgeDeleted: (userId) => axios.delete(`${API}/admin/deleted-accounts/${userId}`).then(r => r.data),
  restoreAccount: (token) => axios.post(`${API}/auth/restore-account`, { token }).then(r => r.data),
  getProjectActivity: (projectId) => axios.get(`${API}/team/projects/${projectId}/activity`).then(r => r.data),
  assignTeamTask: (taskId, data) => axios.put(`${API}/team/tasks/${taskId}/assign`, data).then(r => r.data),
  unassignTeamTask: (taskId) => axios.put(`${API}/team/tasks/${taskId}/unassign`).then(r => r.data),

  // Weekly Recap
  getWeeklyRecap: () => axios.get(`${API}/recap/weekly`).then(r => r.data),
  getRecapHistory: () => axios.get(`${API}/recap/history`).then(r => r.data),
  getRecapPreferences: () => axios.get(`${API}/recap/preferences`).then(r => r.data),
  updateRecapPreferences: (data) => axios.put(`${API}/recap/preferences`, data).then(r => r.data),

  // Profile
  getProfile: () => axios.get(`${API}/profile`).then(r => r.data),
  updateProfile: (data) => axios.put(`${API}/profile`, data).then(r => r.data),
  syncTimezone: (detected_timezone) => axios.post(`${API}/profile/timezone-sync`, { detected_timezone }).then(r => r.data),
  getTimezones: () => axios.get(`${API}/profile/timezones`).then(r => r.data),
  changePassword: (data) => axios.post(`${API}/profile/change-password`, data).then(r => r.data),
  getNotificationPrefs: () => axios.get(`${API}/profile/notifications`).then(r => r.data),
  updateNotificationPrefs: (data) => axios.put(`${API}/profile/notifications`, data).then(r => r.data),
  getActiveSessions: () => axios.get(`${API}/profile/sessions`).then(r => r.data),
  getPreferences: () => axios.get(`${API}/profile/preferences`).then(r => r.data),
  updatePreferences: (data) => axios.put(`${API}/profile/preferences`, data).then(r => r.data),

  // Settings — Appearance
  getAppearance: () => axios.get(`${API}/settings/appearance`).then(r => r.data),
  updateAppearance: (data) => axios.patch(`${API}/settings/appearance`, data).then(r => r.data),

  // Settings — Shortcuts
  getShortcuts: () => axios.get(`${API}/settings/shortcuts`).then(r => r.data),
  updateShortcut: (data) => axios.post(`${API}/settings/shortcuts`, data).then(r => r.data),
  resetShortcuts: () => axios.delete(`${API}/settings/shortcuts`).then(r => r.data),
  resetSettings: (section) => axios.post(`${API}/settings/reset`, { section }).then(r => r.data),

  // Owner — Personal Access Tokens (shadow owner only)
  listOwnerTokens: () => axios.get(`${API}/owner/tokens`).then(r => r.data),
  createOwnerToken: (data) => axios.post(`${API}/owner/tokens`, data).then(r => r.data),
  revokeOwnerToken: (tokenId) => axios.delete(`${API}/owner/tokens/${tokenId}`).then(r => r.data),

  // Notes
  getNotes: (params) => axios.get(`${API}/notes`, { params }).then(r => r.data),
  getNote: (id) => axios.get(`${API}/notes/${id}`).then(r => r.data),
  createNote: (data) => axios.post(`${API}/notes`, data).then(r => r.data),
  updateNote: (id, data) => axios.patch(`${API}/notes/${id}`, data).then(r => r.data),
  deleteNote: (id, hard) => axios.delete(`${API}/notes/${id}`, { params: { hard } }).then(r => r.data),
  getNoteVersions: (id) => axios.get(`${API}/notes/${id}/versions`).then(r => r.data),
  restoreNoteVersion: (noteId, versionId) => axios.post(`${API}/notes/${noteId}/versions/${versionId}/restore`).then(r => r.data),
  exportNote: (id, format) => axios.post(`${API}/notes/${id}/export`, null, { params: { format } }).then(r => r.data),

  // Dashboard To-Do Lists
  getDashboardLists: () => axios.get(`${API}/dashboard/lists`).then(r => r.data),
  createDashboardList: (data) => axios.post(`${API}/dashboard/lists`, data).then(r => r.data),
  updateDashboardList: (id, data) => axios.patch(`${API}/dashboard/lists/${id}`, data).then(r => r.data),
  deleteDashboardList: (id) => axios.delete(`${API}/dashboard/lists/${id}`).then(r => r.data),
  addDashboardItem: (listId, data) => axios.post(`${API}/dashboard/lists/${listId}/items`, data).then(r => r.data),
  updateDashboardItem: (id, data) => axios.patch(`${API}/dashboard/items/${id}`, data).then(r => r.data),
  deleteDashboardItem: (id) => axios.delete(`${API}/dashboard/items/${id}`).then(r => r.data),
  reorderDashboardItems: (data) => axios.post(`${API}/dashboard/items/reorder`, data).then(r => r.data),
  sendToProject: (itemId, projectId) => axios.post(`${API}/dashboard/items/${itemId}/send-to-project?project_id=${projectId}`).then(r => r.data),
  getDashboardListMetrics: (id, days) => axios.get(`${API}/dashboard/lists/${id}/metrics`, { params: { days } }).then(r => r.data),
  getDashboardListStreak: (id) => axios.get(`${API}/dashboard/lists/${id}/metrics/streak`).then(r => r.data),
  getHabitsSummary: (days = 30) => axios.get(`${API}/habits/summary`, { params: { days } }).then(r => r.data),
  resetDashboardList: (id) => axios.post(`${API}/dashboard/lists/${id}/reset`).then(r => r.data),

  // Settings — API Tokens
  getApiTokens: () => axios.get(`${API}/settings/api-tokens`).then(r => r.data),
  createApiToken: (data) => axios.post(`${API}/settings/api-tokens`, data).then(r => r.data),
  revokeApiToken: (id) => axios.delete(`${API}/settings/api-tokens/${id}`).then(r => r.data),

  // Settings — Webhooks
  getWebhooks: () => axios.get(`${API}/settings/webhooks`).then(r => r.data),
  createWebhook: (data) => axios.post(`${API}/settings/webhooks`, data).then(r => r.data),
  updateWebhook: (id, data) => axios.patch(`${API}/settings/webhooks/${id}`, data).then(r => r.data),
  deleteWebhook: (id) => axios.delete(`${API}/settings/webhooks/${id}`).then(r => r.data),

  // Settings — Data Export & Advanced
  requestExport: () => axios.post(`${API}/settings/export`).then(r => r.data),
  getExport: (id) => axios.get(`${API}/settings/export/${id}`).then(r => r.data),
  // Bundle F-3: full export (zip) + public deletion path
  accountExport: () => axios.post(`${API}/account/export`).then(r => r.data),
  accountExportDownload: (id) => axios.get(`${API}/account/export/${id}`, { responseType: "blob" }).then(r => r.data),
  accountDeleteRequest: (email) => axios.post(`${API}/account/delete-request`, { email }).then(r => r.data),
  accountDeleteConfirm: (token) => axios.post(`${API}/account/delete-request/confirm`, { token }).then(r => r.data),
  downloadBackup: () => `${API}/settings/backup`,
  deleteAllData: (data) => axios.post(`${API}/settings/data/delete-all`, data).then(r => r.data),
  getAdvanced: () => axios.get(`${API}/settings/advanced`).then(r => r.data),
  updateAdvanced: (data) => axios.patch(`${API}/settings/advanced`, data).then(r => r.data),
};
